import type { SdkConfig } from '../core/types';
import type { Api } from '../core/api';
import { hashCode, nowMs } from '../core/utils';
import { redactUrl } from '../core/redact';
import { readSession, writeSession } from '../core/storage';
import { log } from '../core/log';

const SESSION_DEDUPE_KEY = 'emb.lastpv';
const SESSION_DEDUPE_MS = 30_000;

/** UTM params we extract from the current URL. */
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;

function parseUtm(): Record<string, string> | undefined {
	if (typeof location === 'undefined' || !location.search) return undefined;
	try {
		const params = new URLSearchParams(location.search);
		const out: Record<string, string> = {};
		for (const key of UTM_KEYS) {
			const value = params.get(key);
			if (value) out[key.slice(4)] = value; // strip "utm_"
		}
		return Object.keys(out).length ? out : undefined;
	} catch {
		return undefined;
	}
}

/** Per-tab dedup so a refresh + load-event race doesn't double-fire. */
function shouldSkipDuplicate(url: string): boolean {
	const stored = readSession(SESSION_DEDUPE_KEY);
	const hash = hashCode(url);
	const now = nowMs();
	if (stored) {
		try {
			const parsed = JSON.parse(stored) as { hash: string; ts: number };
			if (parsed.hash === hash && now - parsed.ts < SESSION_DEDUPE_MS) return true;
		} catch {
			// fall through
		}
	}
	writeSession(SESSION_DEDUPE_KEY, JSON.stringify({ hash, ts: now }));
	return false;
}

function fire(emblue: Api, navigationType: string): void {
	const url = typeof location !== 'undefined' ? location.href : '';
	if (!url || shouldSkipDuplicate(url)) return;
	emblue.track('page_view', {
		url: redactUrl(url),
		url_title: typeof document !== 'undefined' ? document.title : '',
		referrer: typeof document !== 'undefined' ? redactUrl(document.referrer || '') : '',
		utm: parseUtm(),
		navigation_type: navigationType,
	});
}

export function installPageview(config: SdkConfig, emblue: Api): void {
	if (config.coexistWithLegacy) {
		log('pageview: skipped (legacy active, see spec §0)');
		return;
	}
	if (!config.autoTrack.pageview) return;

	const initial = () => fire(emblue, 'load');
	if (typeof document !== 'undefined' && document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initial, { once: true });
	} else {
		initial();
	}

	if (!config.autoTrack.spa) return;
	if (typeof window === 'undefined') return;
	if (window.__emblueHistoryPatched) {
		log('pageview: history hooks already patched (likely by legacy or older v2), skipping');
		return;
	}
	window.__emblueHistoryPatched = true;

	const origPush = history.pushState;
	const origReplace = history.replaceState;
	history.pushState = function (...args: any[]) {
		const r = origPush.apply(this, args as any);
		try {
			fire(emblue, 'pushState');
		} catch (e) {
			log('pushState fire failed', e);
		}
		return r;
	};
	history.replaceState = function (...args: any[]) {
		const r = origReplace.apply(this, args as any);
		try {
			fire(emblue, 'replaceState');
		} catch (e) {
			log('replaceState fire failed', e);
		}
		return r;
	};
	window.addEventListener('popstate', () => fire(emblue, 'popstate'));
}
